import { vehicleApi } from "#/api/vehicleApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import { showSuccessNotification } from "#/components/notifications";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import { getRandomColor } from "#/utils/ColorUtils";
import type { Vehicle } from "#/types/vehicle";
import { Badge, Button, Modal, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconEye, IconPlus, IconTrash } from "@tabler/icons-react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

const VehiclesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite, canDelete } = usePermission();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle>();

  const headers = [
    { key: "id", value: t("vehicles.columns.id") },
    { key: "name", value: t("vehicles.columns.name") },
    { key: "rfid", value: t("vehicles.columns.rfid") },
    { key: "vehicle_model", value: t("vehicles.columns.model") },
    { key: "site", value: t("vehicles.columns.site") },
    { key: "assigned_driver", value: t("vehicles.columns.driver") },
    { key: "maintenance", value: t("vehicles.columns.maintenance") },
    { key: "updatedAt", value: t("vehicles.columns.lastUpdated") },
    { key: "actions", value: t("common.actions") },
  ];
  const [sortBy, setSortBy] = useState<string>("createdAt");

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getVehicles,
  } = useQuery({
    queryKey: ["vehicles", sortBy, page, limit, sortDirection],
    queryFn: async () => {
      try {
        const response = await vehicleApi.getAll({
          limit: limit,
          page: page,
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
        });
        console.log("🚀 ~ VehiclesPage ~ response:", response);
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch (error) {
        console.log("🚀 ~ queryFn: ~ error:", error);
        throw new Error("No vehicles returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  const deleteVehicleMutation = useMutation({
    mutationFn: async (id: number) => {
      return await vehicleApi.delete(id);
    },
    onSuccess: () => {
      showSuccessNotification({
        message: t("vehicles.deleteVehicle.success"),
      });
      closeDeleteModal();
      setVehicleToDelete(undefined);
      void getVehicles();
    },
    onError: (error) => {
      console.log("🚀 ~ deleteVehicle ~ error:", error);
    },
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex items-center justify-between bg-white z-10">
        <Title order={2}>{t("vehicles.title")}</Title>
        <div className="w-fit flex items-center justify-center gap-4">
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => {
                void navigate("/fleet/vehicles/new");
              }}
              color="black"
              size="sm"
              disabled={!canWrite()}
            >
              {t("vehicles.createNewVehicle")}
            </Button>
          </PermissionTooltip>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={headers.map((item) => item.value)}
          loading={isPending}
          allowSorting={true}
          sortableColumns={[1, 2, 3, 4, 5, 6, 7]}
          onSortChange={(e: TableSortProps) => {
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(headers[e.index].key);
          }}
          defaultSort={{
            direction: "DESC",
            index: 7,
          }}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                <Table.Td style={{ minWidth: '100px' }}>{item.id ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '180px' }}>{item.name ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '150px' }}>
                  {item.rfid_tag?.rfid ? (
                    <Badge
                      color={getRandomColor(item.rfid_tag.rfid)}
                      variant="light"
                      size="sm"
                    >
                      {item.rfid_tag.rfid}
                    </Badge>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td style={{ minWidth: '200px' }}>
                  {item.vehicle_model
                    ? `${item.vehicle_model.brand} ${item.vehicle_model.model}`
                    : "--"}
                </Table.Td>
                <Table.Td style={{ minWidth: '150px' }}>{item.site?.name ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '150px' }}>{item.assigned_driver ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '120px' }}>
                  {item.maintenance
                    ? formatDate(item.maintenance, client)
                    : "--"}
                </Table.Td>
                <Table.Td style={{ minWidth: '180px' }}>
                  {item.updatedAt ? (
                    <span>
                      {formatDate(item.updatedAt, client)}
                      <br />
                      {formatTime(item.updatedAt, client)}
                    </span>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td style={{ minWidth: '120px' }}>
                  <div className="w-full flex items-center justify-center gap-2">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/fleet/vehicles/${item.id}`);
                      }}
                    />
                    <PermissionTooltip disabled={!canDelete()}>
                      <ActionButton
                        icon={IconTrash}
                        variant="outline"
                        color="red"
                        onClick={() => {
                          setVehicleToDelete(item);
                          openDeleteModal();
                        }}
                        disabled={!canDelete()}
                      />
                    </PermissionTooltip>
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        title={t("vehicles.deleteVehicle.title")}
        closeOnClickOutside={false}
        classNames={{
          title: "font-bold",
        }}
      >
        <div className="w-full flex flex-col items-center justify-center gap-4">
          <div className="flex flex-col gap-2 text-sm">
            <span>{t("vehicles.deleteVehicle.description")}</span>
            <div className="flex flex-col gap-1 mx-auto my-4 bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 w-full text-center">
              <span className="font-bold">{vehicleToDelete?.name ?? "--"}</span>
              <span className="text-xs text-gray-600">
                {t("vehicles.deleteVehicle.rfid")} {vehicleToDelete?.rfid_tag?.rfid ?? "--"}
              </span>
              {vehicleToDelete?.vehicle_model && (
                <span className="text-xs text-gray-600">
                  {t("vehicles.deleteVehicle.model")} {vehicleToDelete.vehicle_model.brand}{" "}
                  {vehicleToDelete.vehicle_model.model}
                </span>
              )}
            </div>
            <span>
              {t("vehicles.deleteVehicle.warning")}
            </span>

            <span className="font-bold my-2">
              {t("vehicles.deleteVehicle.confirmation")}
            </span>
          </div>

          <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
            <Button size="sm" color="black" onClick={closeDeleteModal}>
              {t("common.cancel")}
            </Button>
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                onClick={() => {
                  if (vehicleToDelete) {
                    deleteVehicleMutation.mutate(vehicleToDelete.id);
                  }
                }}
                color="red"
                size="sm"
                loading={deleteVehicleMutation.isPending}
                leftSection={<IconTrash size={18} />}
                disabled={!canDelete()}
              >
                {t("vehicles.deleteVehicle.confirmButton")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VehiclesPage;
